import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import Seo from '../../components/Seo';
import Turnstile from '../../components/Turnstile';
import StateAutocomplete from '../../components/StateAutocomplete';
import { submitStartFreeApplication, fetchStates, ApiError, type StateOption } from '../../lib/api';
import { startFreeSchema, type StartFreeFormValues } from '../../schemas/startFree';

const TURNSTILE_ENABLED = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;
const SUPPORT_EMAIL = import.meta.env.VITE_SUPPORT_EMAIL || 'support@fbinsights.ai';

const labelClass = 'mb-1.5 block text-[14px] font-semibold text-fb-navy';
const inputClass =
  'w-full rounded-md border border-fb-border px-4 py-3 text-[15px] text-fb-ink outline-none transition-colors placeholder:text-fb-muted focus:border-fb-blue';

export default function StartFreePage() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<StartFreeFormValues>({
    resolver: zodResolver(startFreeSchema),
    mode: 'onBlur',
    defaultValues: {
      legalName: '',
      brandName: '',
      contactName: '',
      email: '',
      locationState: '',
      website: '',
      opportunitiesDescription: '',
      honeypot: '',
    },
  });

  const [token, setToken] = useState('');
  const [resetSignal, setResetSignal] = useState(0);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success'>('idle');
  const [serverError, setServerError] = useState('');
  const [showContact, setShowContact] = useState(false);

  const [states, setStates] = useState<StateOption[]>([]);
  useEffect(() => {
    fetchStates()
      .then(setStates)
      .catch(() => setStates([]));
  }, []);

  const onSubmit = async (values: StartFreeFormValues) => {
    setServerError('');
    setShowContact(false);

    // Honeypot filled → bot. Pretend success without calling the API.
    if (values.honeypot) {
      setSubmittedEmail(values.email);
      setStatus('success');
      return;
    }

    if (TURNSTILE_ENABLED && !token) {
      setServerError('Please complete the captcha.');
      return;
    }

    try {
      await submitStartFreeApplication({
        email: values.email,
        legalName: values.legalName,
        brandName: values.brandName,
        contactName: values.contactName,
        locationState: values.locationState,
        website: values.website,
        opportunitiesDescription: values.opportunitiesDescription || undefined,
        turnstileToken: token || undefined,
      });
      setSubmittedEmail(values.email);
      setStatus('success');
    } catch (err) {
      // The request consumed the Turnstile token — issue a fresh one for any retry.
      if (TURNSTILE_ENABLED) {
        setToken('');
        setResetSignal((n) => n + 1);
      }
      // Duplicate email → surface it on the email field.
      if (err instanceof ApiError && err.status === 409) {
        setError('email', {
          type: 'server',
          message: 'An application with this email already exists.',
        });
        return;
      }
      // Email validation rejected by the server (disposable domain, undeliverable
      // domain, plus addressing, bad format) → surface the server's message on the
      // email field instead of the scary generic error. selfApply's 422s are all
      // email-related; client zod already covers the other required fields.
      if (err instanceof ApiError && err.status === 422) {
        setError('email', {
          type: 'server',
          message: err.message || 'Please enter a valid email address.',
        });
        return;
      }
      // Anything else → generic failure + a way to reach us.
      setServerError('Something went wrong while submitting your application.');
      setShowContact(true);
    }
  };

  const submitDisabled = isSubmitting || (TURNSTILE_ENABLED && !token);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Seo
        title="Start Free · F&B Insights"
        description="Apply to partner with F&B Insights. Tell us about your brand and the opportunities you're after."
        path="/start-free"
      />
      <Navigation scrolled={scrolled} showMenuItems={false} />

      {/* Header band */}
      <header className="bg-fb-navy text-white">
        <div className="mx-auto max-w-[820px] px-8 py-14">
          <div className="mb-4 text-[11px] font-semibold uppercase tracking-[2px] text-fb-cyan">
            Get started
          </div>
          <h1 className="mb-3 text-[42px] font-bold tracking-[-1px]">Partner with F&B Insights</h1>
          <p className="text-[15px] font-light text-white/70">
            Tell us about your brand and the opportunities you&apos;re after. We&apos;ll review your
            application and get back to you shortly.
          </p>
        </div>
      </header>

      <div className="flex-1 pb-24 pt-14">
        <div className="mx-auto max-w-[640px] px-6 sm:px-8">
          {status === 'success' ? (
            <div className="rounded-[14px] border border-fb-border bg-fb-light/40 p-8 text-center">
              <h2 className="mb-2 text-2xl font-bold text-fb-navy">Application received</h2>
              <p className="text-fb-ink">
                Thanks for your interest. We&apos;ve sent a confirmation to{' '}
                <span className="font-semibold">{submittedEmail}</span> and our team will be in
                touch soon.
              </p>
              <Link
                to="/"
                className="mt-6 inline-block rounded-md bg-fb-blue px-6 py-3 text-[15px] font-semibold text-white transition-colors hover:bg-fb-cyan"
              >
                Back to home
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
              {/* Honeypot — hidden from users, tempting for bots. */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
                {...register('honeypot')}
              />

              <div>
                <label htmlFor="legalName" className={labelClass}>
                  Company / brand legal name<span className="text-fb-red"> *</span>
                </label>
                <input
                  id="legalName"
                  type="text"
                  className={inputClass}
                  placeholder="Acme Spirits, LLC"
                  aria-invalid={!!errors.legalName}
                  {...register('legalName')}
                />
                {errors.legalName && (
                  <p className="mt-1 text-[13px] text-fb-red">{errors.legalName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="brandName" className={labelClass}>
                  Brand name<span className="text-fb-red"> *</span>
                </label>
                <input
                  id="brandName"
                  type="text"
                  className={inputClass}
                  placeholder="Your Brand"
                  aria-invalid={!!errors.brandName}
                  {...register('brandName')}
                />
                {errors.brandName && (
                  <p className="mt-1 text-[13px] text-fb-red">{errors.brandName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="contactName" className={labelClass}>
                  Your name<span className="text-fb-red"> *</span>
                </label>
                <input
                  id="contactName"
                  type="text"
                  className={inputClass}
                  placeholder="Jane Doe"
                  aria-invalid={!!errors.contactName}
                  {...register('contactName')}
                />
                {errors.contactName && (
                  <p className="mt-1 text-[13px] text-fb-red">{errors.contactName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className={labelClass}>
                  Work email<span className="text-fb-red"> *</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className={inputClass}
                  placeholder="you@company.com"
                  aria-invalid={!!errors.email}
                  {...register('email')}
                />
                {errors.email && (
                  <p className="mt-1 text-[13px] text-fb-red">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="website" className={labelClass}>
                  Website<span className="text-fb-red"> *</span>
                </label>
                <input
                  id="website"
                  type="text"
                  className={inputClass}
                  placeholder="company.com"
                  aria-invalid={!!errors.website}
                  {...register('website')}
                />
                {errors.website && (
                  <p className="mt-1 text-[13px] text-fb-red">{errors.website.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="locationState" className={labelClass}>
                  Where do you want opportunities? (state)<span className="text-fb-red"> *</span>
                </label>
                <Controller
                  name="locationState"
                  control={control}
                  render={({ field }) => (
                    <StateAutocomplete
                      id="locationState"
                      className={inputClass}
                      states={states}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={!!errors.locationState}
                    />
                  )}
                />
                {errors.locationState && (
                  <p className="mt-1 text-[13px] text-fb-red">{errors.locationState.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="opportunities" className={labelClass}>
                  What opportunities are you after?{' '}
                  <span className="font-normal text-fb-muted">(optional)</span>
                </label>
                <textarea
                  id="opportunities"
                  className={`${inputClass} min-h-[120px] resize-y`}
                  placeholder="Tell us about your brands, target markets, and the placements you're looking for."
                  aria-invalid={!!errors.opportunitiesDescription}
                  {...register('opportunitiesDescription')}
                />
                {errors.opportunitiesDescription && (
                  <p className="mt-1 text-[13px] text-fb-red">
                    {errors.opportunitiesDescription.message}
                  </p>
                )}
              </div>

              {TURNSTILE_ENABLED && (
                <Turnstile
                  onVerify={setToken}
                  onExpire={() => setToken('')}
                  resetSignal={resetSignal}
                />
              )}

              {serverError && (
                <div className="text-[14px] text-fb-red">
                  <p>{serverError}</p>
                  {showContact && (
                    <p>
                      Need help? Contact us at{' '}
                      <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
                        {SUPPORT_EMAIL}
                      </a>
                      .
                    </p>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={submitDisabled}
                className="mt-1 inline-block rounded-md bg-fb-blue px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-fb-cyan disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting…' : 'Submit application'}
              </button>
            </form>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
